import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Input, Button, notify, Page, Card } from 'sha-el-design';
import { MdHome, MdArrowBack } from 'react-icons/md';
import {
  QuestionnaireMutationInput,
  QuestionnaireStateObjectEdge,
  TownNodeEdge,
  QuestionNodeEdge,
} from '../../../../schema';
import { QuestionDropdown } from '../question/QuestionDropdown';
import { Inject, fromGlobalId } from '../../../helpers';
import { QuestionnaireService } from '../../../service/questionnaireService';
import { QuestionnaireStateDropdown } from '../../Common/QuestionnaireStateDropdown';
import { Link } from 'react-router-dom';
import '../../Common/listPageStyles';

/**
 * Add / Edit Questionnaire. EditQuestionnaire renders this same component with
 * the fetched questionnaire as props.
 *
 * The old form initialized state straight from questionnaireSchema.initialize()
 * without copying, then mutated it in place -- so a fresh Add form could come
 * pre-filled with the previous visit's values. It also initialized `questions`
 * as a STRING (the schema declared validate.Text), so submitting without
 * selecting any question crashed on ''.map and left the page stuck on a
 * spinner with Submit locked. And submit hardcoded active:true, silently
 * re-activating any inactive questionnaire you edited.
 */
export class AddQuestionnaire extends React.Component<Props, State> {
  @Inject questionnaireService: QuestionnaireService;

  constructor(props: Props) {
    super(props);
    // Seeded from props ONCE. An earlier version read
    // `selected.length ? selected : props.selected` at render time, which
    // meant clearing the last chip fell back to the props value and the
    // chips reappeared.
    this.state = {
      questionnaire: this.normalize(this.props.questionnaire),
      selectedQuestionnaireState: this.props.selectedQuestionnaireState || null,
      selectedQuestions: this.props.selectedQuestions || [],
      lockSubmit: false,
    };
  }

  normalize = (q: Props['questionnaire']): QuestionnaireFormValue => (q ? {
    id: q.id,
    name: q.name || '',
    state: q.state || null,
    town: (q as any).town || null,
    questions: ((q.questions as any[]) || []).slice(),
    openingStatement: q.openingStatement || '',
    closingStatement: q.closingStatement || '',
    active: (q as any).active === undefined ? true : !!(q as any).active,
  } : {
    name: '',
    state: null,
    town: null,
    questions: [],
    openingStatement: '',
    closingStatement: '',
    active: true,
  })

  isEdit = () => !!this.state.questionnaire.id;

  onChange = (name: keyof QuestionnaireFormValue, value: any) => {
    this.setState({ questionnaire: { ...this.state.questionnaire, [name]: value } });
  }

  clear = () => {
    this.setState({
      questionnaire: this.normalize(this.props.questionnaire),
      selectedQuestionnaireState: this.props.selectedQuestionnaireState || null,
      selectedQuestions: this.props.selectedQuestions || [],
      lockSubmit: false,
    });
  }

  // Duplicate: drop the id so the next save CREATES a copy -- the point is
  // rolling the same script out to another state, so the state must change
  // before saving (the server allows one active questionnaire per state).
  duplicate = () => {
    this.setState({ questionnaire: { ...this.state.questionnaire, id: undefined } });
    notify({
      type: 'info',
      title: 'Editing a copy',
      message: 'Pick the state this copy is for, then save to create it.',
      duration: 4500,
    });
  }

  submit = () => {
    if (this.state.lockSubmit) { return; }
    const { questionnaire } = this.state;

    if (!questionnaire.name.trim()) {
      return notify({ type: 'error', title: 'Name is required', duration: 3000 });
    }
    if (!questionnaire.state) {
      return notify({ type: 'error', title: 'State is required', duration: 3000 });
    }
    if (!questionnaire.questions.length) {
      // The old submit crashed here instead (''.map), freezing the page on a
      // spinner with Submit locked.
      return notify({
        type: 'error',
        title: 'No questions selected',
        message: 'A questionnaire needs at least one question.',
        duration: 3500,
      });
    }

    this.setState({ lockSubmit: true });

    const isEdit = this.isEdit();
    const payload: any = {
      name: questionnaire.name,
      state: questionnaire.state,
      questions: questionnaire.questions.map((v) => fromGlobalId(v)),
      openingStatement: questionnaire.openingStatement,
      closingStatement: questionnaire.closingStatement,
      // Preserve the loaded status on edit; new (and duplicated)
      // questionnaires start active, matching the model default.
      active: isEdit ? questionnaire.active : true,
    };
    if (questionnaire.id) { payload.id = questionnaire.id; }
    if (questionnaire.town) { payload.town = questionnaire.town; }

    this.questionnaireService.add(
      payload,
      (response) => {
        // State is released immediately in every branch -- notify's callBack
        // only fires when the toast CLOSES, which left Submit stuck on
        // 'Saving...' for the whole toast duration.
        if (!response.data.createQuestionnaire && response.errors) {
          this.setState({ lockSubmit: false });
          return notify({
            type: 'error',
            title: response.errors[0].message,
            duration: 3500,
          });
        }

        const errors = response.data.createQuestionnaire.errors;
        if (!errors || !errors.length) {
          if (isEdit) {
            this.setState({ lockSubmit: false });
          } else {
            this.clear();
          }
          notify({
            type: 'success',
            title: isEdit ? 'Questionnaire Updated' : 'Questionnaire Added',
            duration: 2500,
          });
        } else {
          this.setState({ lockSubmit: false });
          const field = errors[0].field;
          notify({
            type: 'error',
            // The serializer's guards (one active questionnaire per state; no
            // edits while the state has open work) come back as
            // nonFieldErrors -- show the sentence, not the field name.
            title: (field === 'nonFieldErrors' || field === 'non_field_errors')
              ? 'Cannot save' : field.toUpperCase(),
            message: errors[0].messages[0],
            duration: 4500,
          });
        }
      },
      // Transport failure (offline, 401, 5xx) used to be swallowed by a
      // literal `(e) => e`, leaving Submit stuck on 'Saving...' forever with
      // nothing in the console.
      () => {
        this.setState({ lockSubmit: false });
        notify({
          type: 'error',
          title: 'Could not reach the server',
          message: 'Nothing was saved. Check your connection and try again.',
          duration: 4500,
        });
      },
    );
  }

  breadcrumbs = () => ({
    seperator: '/',
    paths: [
      { text: <MdHome />, link: '/dashboard' },
      { text: 'Questionnaire', link: '/dashboard/questionnaire' },
      this.isEdit()
        ? { text: 'Edit Questionnaire', link: `/dashboard/questionnaire/${this.state.questionnaire.id}` }
        : { text: 'Add Questionnaire', link: '/dashboard/questionnaire/add' },
    ].map((v) => ({
      render: () => (
        <Link key={v.link} to={v.link}>
          {v.text}
        </Link>
      ),
    })),
  })

  render() {
    const { questionnaire, selectedQuestionnaireState, selectedQuestions, lockSubmit } = this.state;
    const isEdit = this.isEdit();
    const questionCount = questionnaire.questions.length;
    return (
      <div className='cs-form'>
        <Page
          breadcrumbs={this.breadcrumbs()}
          title={`${isEdit ? 'Edit' : 'Add'} Questionnaire`}
          backIcon={
            <Link to={'/dashboard/questionnaire'}>
              <MdArrowBack />
            </Link>
          }
        >
          <div className='cs-form__card'>
            <Card>
              <section className='cs-form__section'>
                <h4 className='cs-form__legend'>Questionnaire</h4>
                <p className='cs-form__hint'>
                  Callers in the chosen state work through this questionnaire. One
                  questionnaire can be active per state at a time.
                </p>
                <div className='cs-form__grid'>
                  <div>
                    <Input
                      required
                      label='Name'
                      value={questionnaire.name}
                      onChange={(e) => this.onChange('name', e.target.value)}
                    />
                  </div>
                  <div>
                    <QuestionnaireStateDropdown
                      value={selectedQuestionnaireState}
                      inputProps={{}}
                      uniqueIdentifier={(v) => v.node.id}
                      displayProp={(v) => v && v.node.name}
                      onChange={(v) => {
                        this.onChange('state', v.node.id);
                        this.setState({ selectedQuestionnaireState: v });
                      }}
                      mode='single'
                      label='State'
                    />
                  </div>
                </div>
              </section>

              <section className='cs-form__section'>
                <h4 className='cs-form__legend'>Questions</h4>
                <p className='cs-form__hint'>
                  Asked in the order they are picked.
                  {questionCount ? ` ${questionCount} selected.` : ' None selected yet.'}
                </p>
                <QuestionDropdown
                  value={selectedQuestions}
                  inputProps={{}}
                  uniqueIdentifier={(v) => v.node.id}
                  displayProp={(v) => v && v.node.name}
                  onChange={(values) => {
                    // Reject a pick that duplicates one already chosen. The
                    // old form of this guard compared the LAST id against the
                    // whole list and required exactly one match, so an empty
                    // selection (`[].pop()` is undefined, 0 matches) was
                    // silently swallowed -- you could never clear the last
                    // question to start over.
                    const ids = values.map((v) => v.node.id);
                    if (new Set(ids).size !== ids.length) { return; }
                    this.onChange('questions', ids);
                    this.setState({ selectedQuestions: values });
                  }}
                  mode='multiple'
                  label='Questions'
                />
              </section>

              <section className='cs-form__section'>
                <h4 className='cs-form__legend'>Call script</h4>
                <p className='cs-form__hint'>
                  What the caller reads out before the first question and after the last one.
                </p>
                <div className='cs-form__grid'>
                  <div>
                    <label className='cs-form__field-label' htmlFor='cs-opening'>
                      Opening statement
                    </label>
                    <textarea
                      id='cs-opening'
                      className='cs-form__textarea'
                      placeholder='Namaskar, I am speaking from…'
                      value={questionnaire.openingStatement}
                      onChange={(e) => this.onChange('openingStatement', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className='cs-form__field-label' htmlFor='cs-closing'>
                      Closing statement
                    </label>
                    <textarea
                      id='cs-closing'
                      className='cs-form__textarea'
                      placeholder='Thank you, we expect your regular support…'
                      value={questionnaire.closingStatement}
                      onChange={(e) => this.onChange('closingStatement', e.target.value)}
                    />
                  </div>
                </div>
              </section>

              <div className='cs-form__actions'>
                {isEdit && (
                  <Button type='secondary' onClick={this.duplicate}>
                    Duplicate
                  </Button>
                )}
                <Button type='secondary' onClick={this.clear}>
                  {isEdit ? 'Reset' : 'Clear'}
                </Button>
                <Button
                  type='primary'
                  onClick={this.submit}
                  disabled={lockSubmit}
                >
                  {lockSubmit ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Questionnaire'}
                </Button>
              </div>
            </Card>
          </div>
        </Page>
      </div>
    );
  }
}

interface QuestionnaireFormValue {
  id?: string;
  name: string;
  state: string;
  town: string;
  questions: any[];
  openingStatement: string;
  closingStatement: string;
  active: boolean;
}

interface Props extends Partial<RouteComponentProps> {
  questionnaire: QuestionnaireMutationInput;
  selectedQuestionnaireState: QuestionnaireStateObjectEdge;
  selectedTown: TownNodeEdge;
  selectedQuestions: QuestionNodeEdge[];
}

interface State {
  questionnaire: QuestionnaireFormValue;
  selectedQuestionnaireState: QuestionnaireStateObjectEdge;
  selectedQuestions: QuestionNodeEdge[];
  lockSubmit: boolean;
}
